import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Loader from '../common/Loader';

const formatDate = (val) => {
  if (val == null) return '—';
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
};

const PAGE_SIZES = [10, 20, 50, 100];
const ROLE_BADGE = { owner: 'badge-orange', admin: 'badge-orange', editor: 'badge-blue', viewer: 'badge-gray' };

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total]);
  for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(`gap-${i}`);
    result.push(sorted[i]);
  }
  return result;
};

const MembersTab = ({ orgId, apiCall, apiCallNoBody }) => {
  const base = `/admin/organizations/${orgId}`;

  // Paginated data
  const [members, setMembers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search (server-side via ?q=, debounced)
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Filters (client-side on current page)
  const [roleFilter, setRoleFilter] = useState('');

  // Sort (client-side on current page)
  const [sortField, setSortField] = useState('email');
  const [sortDir, setSortDir] = useState('asc');

  // Multi-select
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Add member modal
  const [showAdd, setShowAdd] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addRole, setAddRole] = useState('viewer');
  const [addSelectedUser, setAddSelectedUser] = useState(null);
  const [addOnlyUnassigned, setAddOnlyUnassigned] = useState(true);
  // User picker
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerDebounced, setPickerDebounced] = useState('');
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  // Edit role modal
  const [editMember, setEditMember] = useState(null);
  const [editRole, setEditRole] = useState('viewer');
  const [editSaving, setEditSaving] = useState(false);

  // Remove member confirm
  const [removeId, setRemoveId] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  // Bulk remove confirm
  const [bulkRemoveConfirm, setBulkRemoveConfirm] = useState(false);
  const [bulkRemoveLoading, setBulkRemoveLoading] = useState(false);

  // Fetch
  const fetchMembers = async (p = page, s = pageSize, q = debouncedSearch) => {
    setLoading(true);
    setError('');
    try {
      let url = `${base}/members?page=${p}&page_size=${s}`;
      if (q && q.trim()) url += `&q=${encodeURIComponent(q.trim())}`;
      const data = await apiCall(url);
      setMembers(data.members || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchMembers(page, pageSize, debouncedSearch); }, [page, pageSize, debouncedSearch]);

  // Sort & search on current page
  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIndicator = ({ field }) => {
    if (sortField !== field) return <span className="sort-indicator inactive">↕</span>;
    return <span className="sort-indicator">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const displayed = members
    .filter((m) => {
      if (roleFilter && m.role !== roleFilter) return false;
      return true;
    })
    .sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  // Multi-select helpers
  const pageIds = displayed.map((m) => m.user_id || m.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  // Add Member modal: user-picker fetch (debounced, fires on search / toggle change / modal open)
  useEffect(() => {
    const t = setTimeout(() => setPickerDebounced(pickerSearch), 300);
    return () => clearTimeout(t);
  }, [pickerSearch]);

  useEffect(() => {
    if (!showAdd) return;
    const fetchPicker = async () => {
      setPickerLoading(true);
      try {
        let url = `/admin/users?page=1&page_size=20`;
        if (addOnlyUnassigned) url += `&org_id=unassigned`;
        if (pickerDebounced.trim()) url += `&q=${encodeURIComponent(pickerDebounced.trim())}`;
        const data = await apiCall(url);
        setPickerResults(data.users || []);
      } catch (err) {
        setError(err.message);
        setPickerResults([]);
      }
      setPickerLoading(false);
    };
    fetchPicker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdd, pickerDebounced, addOnlyUnassigned]);

  const openAddModal = () => {
    setAddSelectedUser(null);
    setAddRole('viewer');
    setPickerSearch('');
    setPickerDebounced('');
    setPickerResults([]);
    setAddOnlyUnassigned(true);
    setShowAdd(true);
  };

  const selectPickerUser = (user) => {
    setAddSelectedUser(user);
    setPickerSearch('');
  };

  // Handlers
  const handleAdd = async () => {
    if (!addSelectedUser) return;
    setAddSaving(true); setError('');
    try {
      await apiCall(`${base}/members`, {
        method: 'POST',
        body: JSON.stringify({
          email: addSelectedUser.email,
          firebase_uid: addSelectedUser.firebase_uid,
          role: addRole,
          ...(addSelectedUser.display_name ? { display_name: addSelectedUser.display_name } : {}),
        }),
      });
      setShowAdd(false);
      await fetchMembers(page, pageSize);
    } catch (err) { setError(err.message); }
    setAddSaving(false);
  };

  const handleEditRole = async () => {
    setEditSaving(true); setError('');
    try {
      await apiCall(`${base}/members/${editMember.user_id}`, { method: 'PUT', body: JSON.stringify({ role: editRole }) });
      setEditMember(null);
      await fetchMembers(page, pageSize);
    } catch (err) { setError(err.message); }
    setEditSaving(false);
  };

  const handleRemove = async (userId) => {
    setRemoveLoading(true); setError('');
    try {
      await apiCallNoBody(`${base}/members/${userId}`, { method: 'DELETE' });
      setRemoveId(null);
      await fetchMembers(page, pageSize);
    } catch (err) { setError(err.message); }
    setRemoveLoading(false);
  };

  const handleBulkRemove = async () => {
    setBulkRemoveLoading(true); setError('');
    try {
      const result = await apiCall(`${base}/members/bulk`, {
        method: 'DELETE',
        body: JSON.stringify({ user_ids: [...selectedIds] }),
      });
      if (result?.not_found_count > 0) {
        setError(`${result.removed_count} removed, ${result.not_found_count} not found.`);
      }
      setSelectedIds(new Set());
      setBulkRemoveConfirm(false);
      await fetchMembers(page, pageSize);
    } catch (err) { setError(err.message); }
    setBulkRemoveLoading(false);
  };

  return (
    <>
      {error && <div className="error-message">{error}</div>}

      <div className="orgs-section-header" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button onClick={openAddModal} className="btn btn-primary" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
            <PersonAddIcon sx={{ fontSize: '0.875rem' }} /> Add Member
          </button>
          {selectedIds.size > 0 && (
            <button onClick={() => setBulkRemoveConfirm(true)} className="btn btn-danger" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
              <DeleteSweepIcon sx={{ fontSize: '0.875rem' }} /> Remove Selected ({selectedIds.size})
            </button>
          )}
        </div>
        <span className="pagination-info">{total} member(s)</span>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'stretch' }}>
        <div className="search-bar" style={{ flex: 1, marginBottom: 0 }}>
          <div className="search-input-wrapper">
            <SearchIcon className="search-icon" sx={{ fontSize: '1.25rem' }} />
            <input type="text" placeholder="Search by email or name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
            {searchTerm && <button onClick={() => setSearchTerm('')} className="clear-search-btn"><CloseIcon sx={{ fontSize: '1rem' }} /></button>}
          </div>
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="detail-select"
          style={{ minWidth: '8rem', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
        >
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="editor">Editor</option>
          <option value="viewer">Viewer</option>
        </select>
      </div>

      {loading ? <Loader size="small" /> : (
        <div className="table-card">
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th className="th-checkbox">
                    <input type="checkbox" checked={allPageSelected} ref={(el) => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }} onChange={toggleSelectAll} />
                  </th>
                  <th className="th-sortable" onClick={() => handleSort('email')}>Email <SortIndicator field="email" /></th>
                  <th className="th-sortable" onClick={() => handleSort('display_name')}>Name <SortIndicator field="display_name" /></th>
                  <th className="th-sortable" onClick={() => handleSort('user_id')}>User ID <SortIndicator field="user_id" /></th>
                  <th className="th-sortable" onClick={() => handleSort('role')}>Role <SortIndicator field="role" /></th>
                  <th className="th-sortable" onClick={() => handleSort('joined_at')}>Joined <SortIndicator field="joined_at" /></th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayed.length === 0 ? (
                  <tr><td colSpan={7} className="no-results">
                    {debouncedSearch.trim() ? `No results for "${debouncedSearch.trim()}"` : 'No members found'}
                  </td></tr>
                ) : displayed.map((m) => {
                  const id = m.user_id || m.id;
                  return (
                    <tr key={id} className="user-row">
                      <td className="td-checkbox"><input type="checkbox" checked={selectedIds.has(id)} onChange={() => toggleSelect(id)} /></td>
                      <td>{m.email || '—'}</td>
                      <td>{m.display_name || '—'}</td>
                      <td className="td-id" title={id}>{id || '—'}</td>
                      <td><span className={`badge ${ROLE_BADGE[m.role] || 'badge-gray'}`}>{m.role || '—'}</span></td>
                      <td className="td-date">{formatDate(m.joined_at)}</td>
                      <td className="td-actions">
                        <button className="btn-icon-sm btn-shield" title="Edit role" onClick={() => { setEditMember(m); setEditRole(m.role || 'viewer'); }}><EditIcon sx={{ fontSize: '1.125rem' }} /></button>
                        <button className="btn-icon-sm btn-delete" title="Remove" onClick={() => setRemoveId(id)}><PersonRemoveIcon sx={{ fontSize: '1.125rem' }} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="table-footer">
            <div className="rows-per-page">
              <span>Rows per page:</span>
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); setSelectedIds(new Set()); }}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <span className="pagination-info">{total === 0 ? 'No results' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}</span>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn page-nav" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeftIcon sx={{ fontSize: '1.25rem' }} /></button>
                {getPageNumbers(page, totalPages).map((p) => typeof p === 'string' ? <span key={p} className="page-ellipsis">…</span> : <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>)}
                <button className="page-btn page-nav" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRightIcon sx={{ fontSize: '1.25rem' }} /></button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => !addSaving && setShowAdd(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add Member</h3>
              <button onClick={() => setShowAdd(false)} className="btn-close" disabled={addSaving}><CloseIcon sx={{ fontSize: '1.25rem' }} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">User</label>
                {addSelectedUser ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.625rem 0.875rem', background: '#1a1a2e', border: '1px solid #374151', borderRadius: '0.5rem' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: 'white', fontSize: '0.9375rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {addSelectedUser.email || '—'}
                        {addSelectedUser.display_name && <span style={{ color: '#9ca3af', fontWeight: 400 }}> · {addSelectedUser.display_name}</span>}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.125rem', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {addSelectedUser.firebase_uid || addSelectedUser.id || '—'}
                      </div>
                    </div>
                    <button className="btn-icon-sm" onClick={() => setAddSelectedUser(null)} title="Change user" disabled={addSaving}>
                      <CloseIcon sx={{ fontSize: '1rem' }} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="search-input-wrapper">
                      <SearchIcon className="search-icon" sx={{ fontSize: '1.25rem' }} />
                      <input
                        type="text"
                        value={pickerSearch}
                        onChange={(e) => setPickerSearch(e.target.value)}
                        className="search-input"
                        placeholder="Search users by email or name..."
                      />
                      {pickerSearch && (
                        <button onClick={() => setPickerSearch('')} className="clear-search-btn">
                          <CloseIcon sx={{ fontSize: '1rem' }} />
                        </button>
                      )}
                    </div>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.5rem', fontSize: '0.8125rem', color: '#9ca3af', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={addOnlyUnassigned}
                        onChange={(e) => setAddOnlyUnassigned(e.target.checked)}
                        style={{ accentColor: '#f97316' }}
                      />
                      Show only unassigned users
                    </label>
                    <div style={{ marginTop: '0.5rem', maxHeight: '14rem', overflowY: 'auto', border: '1px solid #1a1a2e', borderRadius: '0.5rem', background: '#0a0a16' }}>
                      {pickerLoading ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280', fontSize: '0.8125rem' }}>Loading…</div>
                      ) : pickerResults.length === 0 ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280', fontSize: '0.8125rem' }}>
                          {pickerDebounced.trim() ? `No users matching "${pickerDebounced.trim()}"` : 'No users found'}
                        </div>
                      ) : (
                        pickerResults.map((u) => (
                          <button
                            key={u.firebase_uid || u.id}
                            type="button"
                            onClick={() => selectPickerUser(u)}
                            style={{
                              display: 'block', width: '100%', textAlign: 'left',
                              padding: '0.625rem 0.875rem', background: 'transparent', border: 'none',
                              borderBottom: '1px solid #111128', color: '#d1d5db', cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#1a1a2e'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ color: 'white', fontSize: '0.875rem' }}>
                              {u.email || '—'}
                              {u.display_name && <span style={{ color: '#9ca3af', fontWeight: 400 }}> · {u.display_name}</span>}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.125rem', fontFamily: 'monospace' }}>
                              {u.firebase_uid || u.id || '—'}
                              {u.org_id && <span style={{ marginLeft: '0.5rem', color: '#fbbf24', fontFamily: 'inherit' }}>• already in an org</span>}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Role</label>
                <select value={addRole} onChange={(e) => setAddRole(e.target.value)} className="detail-select" style={{ width: '100%' }}>
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>
            <div className="modal-footer modal-footer-right">
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)} disabled={addSaving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={addSaving || !addSelectedUser}>
                {addSaving ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {editMember && (
        <div className="modal-overlay" onClick={() => !editSaving && setEditMember(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Edit Member Role</h3><button onClick={() => setEditMember(null)} className="btn-close" disabled={editSaving}><CloseIcon sx={{ fontSize: '1.25rem' }} /></button></div>
            <div className="modal-body">
              <div className="detail-row"><span className="detail-label">User:</span><span className="detail-value">{editMember.email || editMember.user_id}</span></div>
              <div className="detail-row" style={{ marginTop: '0.5rem' }}><span className="detail-label">Role:</span><select className="detail-select" value={editRole} onChange={(e) => setEditRole(e.target.value)}><option value="admin">Admin</option><option value="editor">Editor</option><option value="viewer">Viewer</option></select></div>
            </div>
            <div className="modal-footer modal-footer-right">
              <button className="btn btn-secondary" onClick={() => setEditMember(null)} disabled={editSaving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEditRole} disabled={editSaving}>{editSaving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Confirm */}
      {removeId && (
        <div className="modal-overlay" onClick={() => !removeLoading && setRemoveId(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Remove Member</h3></div>
            <div className="modal-body">
              <p className="confirm-text">Remove member <strong className="user-id-highlight">{removeId}</strong>?</p>
              <p className="confirm-subtext">The member will be deactivated.</p>
            </div>
            <div className="modal-footer modal-footer-right">
              <button className="btn btn-secondary" onClick={() => setRemoveId(null)} disabled={removeLoading}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleRemove(removeId)} disabled={removeLoading}>{removeLoading ? 'Removing...' : 'Remove'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Remove Confirm */}
      {bulkRemoveConfirm && (
        <div className="modal-overlay" onClick={() => !bulkRemoveLoading && setBulkRemoveConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Remove Members</h3></div>
            <div className="modal-body">
              <p className="confirm-text">Remove <strong className="user-id-highlight">{selectedIds.size}</strong> selected member(s)?</p>
              <p className="confirm-subtext">This will deactivate all selected members. This action cannot be undone.</p>
            </div>
            <div className="modal-footer modal-footer-right">
              <button className="btn btn-secondary" onClick={() => setBulkRemoveConfirm(false)} disabled={bulkRemoveLoading}>Cancel</button>
              <button className="btn btn-danger" onClick={handleBulkRemove} disabled={bulkRemoveLoading}>{bulkRemoveLoading ? 'Removing...' : `Remove ${selectedIds.size} Member(s)`}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MembersTab;
